/**
 * demo-data.ts — ข้อมูลสังเคราะห์สำหรับ Dashboard demo (ไม่มีข้อมูลจริง)
 * ใช้ engine เดียวกับ backend (@rrp/shared) — ตัวเลขจึงตรงกับ P&L จริงเมื่อต่อ API
 */
import type { PnlEvents, SaleEvent, CostEvent } from '@rrp/shared';

const DAYS = 30; // เมษายน 2025

function dateKey(day: number): string {
  return `2025-04-${String(day).padStart(2, '0')}`;
}

/** สร้างยอดขายรายวันแบบมี trend (สังเคราะห์) */
function dailySales(scale: number, withMissingCost: boolean): SaleEvent[] {
  const out: SaleEvent[] = [];
  for (let d = 1; d <= DAYS; d++) {
    const wave = 1 + 0.35 * Math.sin((d / DAYS) * Math.PI * 2);
    const revenue = Math.round(15000 * scale * wave);
    const cost = Math.round(revenue * 0.42);
    // วันที่ 22 มี SKU ไม่มีต้นทุน (โชว์ completeness incomplete)
    const missing = withMissingCost && d === 22;
    out.push({
      date: dateKey(d),
      revenue: revenue.toString(),
      cost: missing ? null : cost.toString(),
    });
  }
  return out;
}

function costs(scale: number): CostEvent[] {
  const mid = dateKey(15);
  const c = (category: CostEvent['category'], amount: number): CostEvent => ({
    date: mid,
    category,
    amount: Math.round(amount * scale).toString(),
  });
  return [
    c('other_income', 8000),
    c('marketing_expenses', 45000),
    c('commissions_platform_fees', 32000),
    c('transportation_fulfillment', 28000),
    c('payroll_employee', 60000),
    c('accounting_professional', 8000),
    c('fixed_cost', 25000),
    c('variable_cost', 9000),
    c('other_operating', 6000),
    c('depreciation_amortization', 7000),
    c('interest_finance', 3000),
  ];
}

export const demoCurrent: PnlEvents = {
  sales: dailySales(1, true),
  refunds: [
    { date: dateKey(20), amount: '12000' },
    { date: dateKey(27), amount: '5400' },
  ],
  costs: costs(1),
  corporateIncomeTax: '18000',
};

/** เดือนก่อน (มีนาคม) — สเกลต่ำกว่าเล็กน้อยเพื่อโชว์ comparison */
export const demoPrevMonth: PnlEvents = {
  sales: dailySales(0.88, false).map((s) => ({ ...s, date: s.date.replace('2025-04', '2025-03') })),
  refunds: [{ date: '2025-03-18', amount: '9000' }],
  costs: costs(0.9).map((c) => ({ ...c, date: c.date.replace('2025-04', '2025-03') })),
  corporateIncomeTax: '15000',
};

/** ยอดขายรายวัน (สำหรับกราฟ trend) */
export function dailySalesSeries(): { date: string; value: number }[] {
  const byDay = new Map<string, number>();
  for (const s of demoCurrent.sales) {
    byDay.set(s.date, (byDay.get(s.date) ?? 0) + Number(s.revenue));
  }
  for (const r of demoCurrent.refunds) {
    byDay.set(r.date, (byDay.get(r.date) ?? 0) - Number(r.amount));
  }
  return [...byDay.entries()].sort().map(([date, value]) => ({ date, value }));
}
